import React, { createContext, useContext, useEffect, useState } from "react";
import { DIRECTUS_URL } from "@/providers/directus";

export interface BrandConfig {
  name: string;
  descriptor?: string | null;
  logoUrl: string;
  faviconUrl: string;
  appleTouchIconUrl: string;
  formatTitle: (pageTitle?: string) => string;
}

export const DEFAULT_BRAND: BrandConfig = {
  name: "Cordite",
  descriptor: "Sweat & Sterling",
  logoUrl: "/logo.png",
  faviconUrl: "/logo.png",
  appleTouchIconUrl: "/logo.png",
  formatTitle: (pageTitle?: string) => {
    if (!pageTitle || pageTitle === "Cordite") return "Cordite";
    return `${pageTitle} | Cordite`;
  },
};

interface ServerProjectInfo {
  project_name?: string | null;
  project_descriptor?: string | null;
  project_logo?: string | null;
  public_favicon?: string | null;
  public_foreground?: string | null;
}

const BrandContext = createContext<BrandConfig>(DEFAULT_BRAND);

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrand] = useState<BrandConfig>(() => {
    // Check build-time env vars if present as defaults
    const envName = import.meta.env?.VITE_BRAND_NAME;
    const envDescriptor = import.meta.env?.VITE_BRAND_DESCRIPTOR;
    const envLogo = import.meta.env?.VITE_BRAND_LOGO;
    const envFavicon = import.meta.env?.VITE_BRAND_FAVICON;
    const name = envName || DEFAULT_BRAND.name;
    return {
      name,
      descriptor: envDescriptor !== undefined ? envDescriptor : DEFAULT_BRAND.descriptor,
      logoUrl: envLogo || DEFAULT_BRAND.logoUrl,
      faviconUrl: envFavicon || DEFAULT_BRAND.faviconUrl,
      appleTouchIconUrl: envLogo || DEFAULT_BRAND.appleTouchIconUrl,
      formatTitle: (pageTitle?: string) => {
        if (!pageTitle || pageTitle === name) {
          return name && name !== "Cordite" ? `${name} | Cordite` : "Cordite";
        }
        if (name && name !== "Cordite") {
          return `${pageTitle} | ${name} | Cordite`;
        }
        return `${pageTitle} | Cordite`;
      },
    };
  });

  useEffect(() => {
    let cancelled = false;

    async function loadBrand() {
      try {
        const res = await fetch(`${DIRECTUS_URL}/server/info`);
        if (!res.ok) return;
        const json = await res.json();
        const p: ServerProjectInfo = json?.data?.project || {};

        if (cancelled) return;

        // "Directus" is Directus's default unconfigured project_name.
        // If it's set to anything else (or custom brand env is provided), treat as custom brand.
        const customName =
          p.project_name && p.project_name !== "Directus"
            ? p.project_name
            : import.meta.env?.VITE_BRAND_NAME || DEFAULT_BRAND.name;

        const descriptor =
          p.project_descriptor !== undefined && p.project_descriptor !== null
            ? p.project_descriptor
            : customName !== DEFAULT_BRAND.name
            ? ""
            : DEFAULT_BRAND.descriptor;

        const logo = p.project_logo
          ? `${DIRECTUS_URL}/assets/${p.project_logo}`
          : import.meta.env?.VITE_BRAND_LOGO || DEFAULT_BRAND.logoUrl;

        const favicon = p.public_favicon
          ? `${DIRECTUS_URL}/assets/${p.public_favicon}`
          : p.project_logo
          ? `${DIRECTUS_URL}/assets/${p.project_logo}`
          : import.meta.env?.VITE_BRAND_FAVICON || DEFAULT_BRAND.faviconUrl;

        const appleTouch = p.public_favicon
          ? `${DIRECTUS_URL}/assets/${p.public_favicon}`
          : p.project_logo
          ? `${DIRECTUS_URL}/assets/${p.project_logo}`
          : import.meta.env?.VITE_BRAND_LOGO || DEFAULT_BRAND.appleTouchIconUrl;

        const formatTitle = (pageTitle?: string) => {
          if (!pageTitle) {
            return customName && customName !== "Cordite"
              ? `${customName} | Cordite`
              : "Cordite";
          }
          if (customName && customName !== "Cordite") {
            return `${pageTitle} | ${customName} | Cordite`;
          }
          return `${pageTitle} | Cordite`;
        };

        setBrand({
          name: customName,
          descriptor,
          logoUrl: logo,
          faviconUrl: favicon,
          appleTouchIconUrl: appleTouch,
          formatTitle,
        });

        // Update document title if needed
        document.title = formatTitle(document.title.split(" | ")[0] || "");

        // Dynamically update favicon and apple-touch-icon in DOM
        const faviconLink = document.querySelector<HTMLLinkElement>("link[rel='icon']");
        if (faviconLink) {
          faviconLink.href = favicon;
        }

        const appleLink = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
        if (appleLink) {
          appleLink.href = appleTouch;
        }
      } catch {
        /* ignore fetch errors, keep current brand */
      }
    }

    loadBrand();

    return () => {
      cancelled = true;
    };
  }, []);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandConfig {
  return useContext(BrandContext);
}
